/**
 * FlowTile — Mixpanel-style sankey path/flow chart.
 *
 * This is a HAND-ROLLED HTML+SVG renderer (no Highcharts). The DOM structure
 * mirrors Mixpanel's own sankey component verbatim — see the inspector paste
 * the owner shared. Classnames + the colour CSS vars (`--breakdown-bg-color`
 * etc.) match Mixpanel so this would slot into their stylesheet 1:1.
 *
 * Backend response shape (see Event/controllers/v2/board-tile.controller.js):
 *   {
 *     chart: 'sankey',
 *     steps: [{ code, event, before, after, anchor_users }, …],
 *     anchor_event, steps_before, steps_after,
 *     top_n_per_step,
 *     anchor_users,
 *     ribbons: [{ anchor_index, step, from, to, users }, …]
 *   }
 *
 * Where `step` is the anchor-relative offset of the *source* column for that
 * ribbon — so step=-1 means "ribbon from col(anchor-1) → col(anchor)".
 */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Box, Text, Tooltip, IconButton } from '@chakra-ui/react';
import { FiPlus, FiActivity, FiGrid, FiArrowDownCircle, FiArrowRight } from 'react-icons/fi';
import { PRIMARY_BG } from '../../../theme/brand';

const DROP_SENTINEL = '$drop';
const DROP_LABEL    = 'Drop-off';
const OTHER_LABEL   = 'Other events';

// Mixpanel palette — verified by piercing <mp-sankey-chart>'s shadow DOM
// and reading `getComputedStyle(documentElement).getPropertyValue('--gray3')`
// plus the rendered fills on .sankey-node-breakdown and svg.sankey-links path.
const C_INDIGO     = PRIMARY_BG;  // xNotify brand blue — matches active link in top nav (was Mixpanel purple #7856FF)
const C_CORAL      = '#FF7557';  // Mixpanel anchor-internal drop coral
const C_GREY3      = '#8F8F91';  // Mixpanel --gray3 (resting ribbon + dropoff dest bar + count text)
const C_GRAY1      = '#F6F6F6';  // Mixpanel --gray1 (header background + chevron pseudo-element)
const C_TEXT_DARK  = '#2A2A2F';  // Mixpanel --gray5 (primary text)
const C_TEXT_LIGHT = '#FFFFFF';
const C_LIFT_POS   = '#219464';  // Mixpanel positive lift green (rgb(33,148,100))
const C_LIFT_NEG   = '#E34F2F';  // Mixpanel --red1 (negative lift)
const RIBBON_REST_OPACITY = 0.10; // Mixpanel default fill-opacity on svg.sankey-links path
const RIBBON_HOVER_OPACITY = 0.6;

function fmtCompact(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const x = Number(n);
  if (Math.abs(x) >= 1e9) return (x / 1e9).toFixed(2) + 'B';
  if (Math.abs(x) >= 1e6) return (x / 1e6).toFixed(2) + 'M';
  if (Math.abs(x) >= 1e3) return (x / 1e3).toFixed(1) + 'K';
  return Number.isInteger(x) ? x.toLocaleString() : x.toFixed(2);
}

function fmtCount(n) {
  if (n == null) return '—';
  return Math.round(Number(n)).toLocaleString();
}

function fmtPct(p) {
  if (p == null || !Number.isFinite(p)) return '0%';
  if (p >= 10)   return p.toFixed(2) + '%';
  if (p >= 1)    return p.toFixed(2) + '%';
  return p.toFixed(3) + '%';
}

/**
 * Layout the per-column node lists from backend ribbons.
 *
 * Returns:
 *   bands: [{ code, event, anchor_users, startCol, anchorCol, endCol }, …]
 *   columns: [{ colIdx, isAnchor, bandIdx, headerCode, headerEvent, total,
 *               nodes: [{ event, total, continueW, dropW, isAnchor, isDrop, isOther }] }]
 *   edges: [{ fromCol, fromEvent, toCol, toEvent, weight, isToDrop }]
 */
function buildLayout(data, topNDefault) {
  const stepsList = Array.isArray(data?.steps) && data.steps.length > 0
    ? data.steps
    : [{
        code: 'A',
        event: data?.anchor_event || 'event',
        before: data?.steps_before ?? 3,
        after:  data?.steps_after  ?? 3,
        anchor_users: data?.anchor_users ?? null,
      }];

  const bands = [];
  let cursor = 0;
  for (let i = 0; i < stepsList.length; i++) {
    const s = stepsList[i];
    const w = (s.before ?? 0) + (s.after ?? 0) + 1;
    bands.push({
      code: s.code || String.fromCharCode(65 + i),
      event: s.event,
      anchor_users: s.anchor_users,
      before: s.before ?? 0,
      after: s.after ?? 0,
      startCol: cursor,
      anchorCol: cursor + (s.before ?? 0),
      endCol: cursor + w - 1,
    });
    cursor += w;
  }
  const totalCols = cursor;

  // Build edges (fromCol, fromEvent, toCol, toEvent, weight).
  const rawEdges = [];
  for (const r of (data?.ribbons || [])) {
    const band = bands[r.anchor_index || 0];
    const fromCol = band.startCol + band.before + r.step;
    const toCol   = fromCol + 1;
    if (fromCol < 0 || toCol > band.endCol) continue;
    rawEdges.push({
      fromCol,
      toCol,
      fromEvent: r.from,
      toEvent:   r.to,
      weight:    r.users,
    });
  }

  // Per-column event totals (sum of incoming + outgoing) — drives top-N pick.
  const eventWeight = new Map(); // `${col}__${event}` → users
  const bumpW = (col, ev, w) => {
    const k = `${col}__${ev}`;
    eventWeight.set(k, (eventWeight.get(k) || 0) + w);
  };
  for (const e of rawEdges) {
    if (e.fromEvent !== DROP_SENTINEL) bumpW(e.fromCol, e.fromEvent, e.weight);
    if (e.toEvent   !== DROP_SENTINEL) bumpW(e.toCol,   e.toEvent,   e.weight);
  }

  // Top-N per column, always keeping the band's anchor event in its anchor col.
  const topPerCol = new Map();
  const colBand = [];
  for (const b of bands) for (let c = b.startCol; c <= b.endCol; c++) colBand[c] = b;
  for (let col = 0; col < totalCols; col++) {
    const entries = [];
    for (const [k, w] of eventWeight.entries()) {
      const [s, ev] = k.split(/__(.+)/);
      if (Number(s) === col) entries.push([ev, w]);
    }
    entries.sort((a, b) => b[1] - a[1]);
    const kept = new Set(entries.slice(0, topNDefault).map((e) => e[0]));
    const band = colBand[col];
    if (band && col === band.anchorCol) kept.add(band.event);
    topPerCol.set(col, kept);
  }

  // Fold non-top events into 'Other events', and `$drop` into 'Drop-off'.
  const labelOf = (col, ev) => {
    if (ev === DROP_SENTINEL) return DROP_LABEL;
    if (topPerCol.get(col) && topPerCol.get(col).has(ev)) return ev;
    return OTHER_LABEL;
  };

  // Aggregate folded edges.
  const edgeMap = new Map();
  for (const e of rawEdges) {
    const f = labelOf(e.fromCol, e.fromEvent);
    const t = labelOf(e.toCol,   e.toEvent);
    const k = `${e.fromCol}|${f}→${e.toCol}|${t}`;
    const cur = edgeMap.get(k);
    if (cur) cur.weight += e.weight;
    else edgeMap.set(k, {
      fromCol: e.fromCol, fromEvent: f,
      toCol: e.toCol,     toEvent: t,
      weight: e.weight,
      isToDrop: t === DROP_LABEL,
    });
  }
  const edges = [...edgeMap.values()];

  // Per-column nodes — events that appear as `to` of some edge into this col,
  // plus the leftmost column's nodes (only appear as `from`).
  const nodesByCol = new Map();
  for (let col = 0; col < totalCols; col++) nodesByCol.set(col, new Map());
  const ensureNode = (col, ev) => {
    const m = nodesByCol.get(col);
    if (!m.has(ev)) m.set(ev, { event: ev, total: 0, continueW: 0, dropW: 0 });
    return m.get(ev);
  };

  // Each ribbon contributes to the SOURCE node's outgoing (split into continue
  // vs drop) and to the DESTINATION node's incoming. Per Mixpanel:
  //   sourceNode.total ≈ sum of outgoing ribbons (incl. ones that go to drop)
  //   destNode.total   = sum of incoming ribbons
  // For the leftmost column the anchor's total = anchor_users (we override).
  for (const e of edges) {
    const src = ensureNode(e.fromCol, e.fromEvent);
    src.total += e.weight;
    if (e.isToDrop) src.dropW += e.weight;
    else            src.continueW += e.weight;

    const dst = ensureNode(e.toCol, e.toEvent);
    dst.total += 0; // counted via destSums below to avoid double-count
  }
  // Recompute destination totals = sum of weights INTO that col,event.
  const destSums = new Map();
  for (const e of edges) {
    const k = `${e.toCol}|${e.toEvent}`;
    destSums.set(k, (destSums.get(k) || 0) + e.weight);
  }
  for (const [colKey, weight] of destSums.entries()) {
    const [colStr, ev] = colKey.split(/\|(.+)/);
    const node = ensureNode(Number(colStr), ev);
    // For non-leftmost columns, total = incoming. Source-side total above only
    // applies when this node has NO incoming (i.e. leftmost column).
    node.total = weight;
  }

  // Override anchor totals — leftmost anchor uses anchor_users directly.
  for (const b of bands) {
    const anchorNode = nodesByCol.get(b.anchorCol).get(b.event);
    if (anchorNode && b.anchor_users != null && b.anchor_users > 0) {
      anchorNode.total = b.anchor_users;
    }
  }

  // Materialise columns with ordered node lists. Node ordering inside a column:
  // anchor first (if this is the anchor col), then by total desc, with
  // 'Other events' and 'Drop-off' pinned to the bottom in that order.
  const columns = [];
  for (let col = 0; col < totalCols; col++) {
    const band = colBand[col];
    const isAnchor = band && col === band.anchorCol;
    const offset = col - (band.startCol + band.before);
    const headerCode = offset === 0 ? band.code
      : offset < 0 ? `${band.code} - ${Math.abs(offset)}`
      :              `${band.code} + ${offset}`;
    const headerEvent = offset === 0 ? band.event : '';

    const m = nodesByCol.get(col);
    // Drop-off node suppression rules:
    //   • Pre-anchor columns (offset < 0): never show drop-off — it would mean
    //     "user didn't yet do anything that many steps back", which isn't a
    //     meaningful drop-off bucket.
    //   • Single-anchor flows (only one step total): the user already DID the
    //     anchor event. There is no "Drop-off" from a step they completed.
    //     What we'd label drop-off in subsequent columns is actually "had no
    //     further recorded event in the window" — data sparsity, not a
    //     conversion failure. So we hide it.
    //   • Multi-anchor flows: drop-off at the next-anchor's column means
    //     "didn't reach that anchor" — semantically valid, keep it.
    const isBeforeAnchor = offset < 0;
    const isSingleAnchor = stepsList.length === 1;
    const hideDrop = isBeforeAnchor || isSingleAnchor;
    const nodeList = [...m.values()]
      .filter((n) => n.total > 0)
      .filter((n) => !(hideDrop && n.event === DROP_LABEL))
      .map((n) => ({
        ...n,
        // Single-anchor mode: zero out the in-bar drop split too. Without a
        // next anchor to compare against, "X% dropped" inside each bar means
        // the same thing as the Drop-off destination node we just hid — and
        // showing one without the other is inconsistent. Bars render solid
        // indigo, sized purely by total users at this node.
        dropW: isSingleAnchor ? 0 : n.dropW,
        isAnchor: isAnchor && n.event === band.event,
        isDrop:   n.event === DROP_LABEL,
        isOther:  n.event === OTHER_LABEL,
      }));

    nodeList.sort((a, b) => {
      if (a.isAnchor && !b.isAnchor) return -1;
      if (b.isAnchor && !a.isAnchor) return 1;
      const av = a.isDrop ? 3 : a.isOther ? 2 : 0;
      const bv = b.isDrop ? 3 : b.isOther ? 2 : 0;
      if (av !== bv) return av - bv;
      return b.total - a.total;
    });

    // Compute per-node LIFT — how much more (or less) common this event is at
    // this step compared to the column's average. Baseline = mean total across
    // the non-anchor, non-aggregate nodes in this column (the actual ranked
    // events). A node at 2x the mean ⇒ +100% lift (twice as common); a node
    // at half ⇒ -50% lift (half as common). Anchor / Drop-off / Other are
    // excluded from the baseline calculation because they're not ranked events.
    const baselineNodes = nodeList.filter((n) => !n.isAnchor && !n.isDrop && !n.isOther);
    const baseline = baselineNodes.length > 0
      ? baselineNodes.reduce((s, n) => s + n.total, 0) / baselineNodes.length
      : 0;
    for (const n of nodeList) {
      if (n.isAnchor || n.isDrop || n.isOther || baseline <= 0) {
        n.lift = null;
        continue;
      }
      n.lift = ((n.total - baseline) / baseline) * 100;
    }

    // Two separate denominators:
    //   • pctDenom = anchor user count → drives the % shown in each label
    //     ("1.73% 12,974" reads as "1.73% of salary recipients did this").
    //   • sizeDenom = local column sum → drives bar HEIGHT so each non-anchor
    //     column fills its available vertical space proportionally instead of
    //     reading as tiny stripes (1% of 750K = a bar only 1% of column tall).
    //     The anchor column's only node IS its full anchor count, so its bar
    //     fills its column — and every other column tops out at the same
    //     height as the anchor bar (the visual "not bigger than" constraint).
    const localSum = nodeList.reduce((s, n) => s + n.total, 0);
    const anchorTotal = (band && band.anchor_users && band.anchor_users > 0)
      ? band.anchor_users : localSum;
    columns.push({
      colIdx: col,
      isAnchor,
      bandIdx: bands.indexOf(band),
      bandCode: band.code,
      headerCode,
      headerEvent,
      total: Math.max(localSum, 1),         // bar-height denominator
      pctDenom: Math.max(anchorTotal, 1),   // percentage denominator
      nodes: nodeList,
    });
  }

  return { bands, columns, edges };
}

/**
 * Given a laid-out columns array and per-column pixel geometry, compute the
 * (x,y,height) box for every node. Returns a Map keyed by `${col}|${event}`.
 */
function placeNodes(columns, geom) {
  const positions = new Map();
  for (const col of columns) {
    const x = geom.colOffsets[col.colIdx];
    // Hard rule: every node reserves geom.labelH for its text card. Bars get
    // what's left of the column band proportionally to node weight, with a
    // 4 px minimum so tiny nodes still render. CRITICAL: we must subtract
    // colTop (the chevron-header reserved strip at the top of the band) so
    // the bars actually fit inside the chart container — otherwise nodes at
    // the bottom of busy columns get clipped by the container's overflow.
    const labelTotal = col.nodes.length * geom.labelH;
    const gapTotal   = Math.max(0, col.nodes.length - 1) * geom.nodeGap;
    const bandRoom   = Math.max(0, geom.colHeight - geom.colTop);
    const usable     = Math.max(col.nodes.length * 4, bandRoom - labelTotal - gapTotal);
    const totalW     = Math.max(1, col.total);
    let y = geom.colTop;
    for (const n of col.nodes) {
      const barH = Math.max(4, Math.round((n.total / totalW) * usable));
      const barY = y + geom.labelH;
      positions.set(`${col.colIdx}|${n.event}`, {
        x, y, labelY: y, barY, h: barH,
        width: geom.nodeWidth,
        right: x + geom.nodeWidth,
      });
      y = barY + barH + geom.nodeGap;
    }
  }
  return positions;
}

/**
 * Build SVG path strings for each edge. For each edge we slice the source
 * node's right edge and the destination node's left edge proportional to the
 * edge's share of that node's outgoing (resp. incoming) flow.
 *
 * Path shape (matches Mixpanel exactly): straight extension + cubic bezier +
 * straight extension on both ends, drawn as a closed filled shape.
 */
function buildEdgePaths(columns, edges, positions, geom) {
  // For each source node: total OUT weight. For each dest node: total IN weight.
  const outW = new Map(); // `${col}|${event}` → weight
  const inW  = new Map();
  for (const e of edges) {
    const sk = `${e.fromCol}|${e.fromEvent}`;
    const dk = `${e.toCol}|${e.toEvent}`;
    outW.set(sk, (outW.get(sk) || 0) + e.weight);
    inW.set(dk,  (inW.get(dk)  || 0) + e.weight);
  }

  // Sort edges so that ribbons leaving a node are stacked in deterministic
  // order matching the destination node's vertical order — that's what
  // makes the sankey look clean (no crossing ribbons within a node).
  const colOrder = new Map(); // (col,event) → vertical index
  for (const col of columns) {
    col.nodes.forEach((n, i) => colOrder.set(`${col.colIdx}|${n.event}`, i));
  }

  const sortedEdges = [...edges].sort((a, b) => {
    if (a.fromCol !== b.fromCol) return a.fromCol - b.fromCol;
    const ai = colOrder.get(`${a.fromCol}|${a.fromEvent}`) ?? 0;
    const bi = colOrder.get(`${b.fromCol}|${b.fromEvent}`) ?? 0;
    if (ai !== bi) return ai - bi;
    const aj = colOrder.get(`${a.toCol}|${a.toEvent}`) ?? 0;
    const bj = colOrder.get(`${b.toCol}|${b.toEvent}`) ?? 0;
    return aj - bj;
  });

  // Cursor for each side of each node — tracks how much of its edge we've
  // already allocated to upstream ribbons.
  const srcCursor = new Map(); // (col,event) → next y offset within node
  const dstCursor = new Map();
  const advance = (m, k) => { const v = m.get(k) || 0; return v; };

  const paths = [];
  for (const e of sortedEdges) {
    const src = positions.get(`${e.fromCol}|${e.fromEvent}`);
    const dst = positions.get(`${e.toCol}|${e.toEvent}`);
    if (!src || !dst) continue;
    const totalOut = outW.get(`${e.fromCol}|${e.fromEvent}`) || 1;
    const totalIn  = inW.get(`${e.toCol}|${e.toEvent}`)     || 1;
    const srcH = (e.weight / totalOut) * src.h;
    const dstH = (e.weight / totalIn)  * dst.h;
    // Ribbons attach to the BAR's y span (barY..barY+h), not the wrapper's.
    const sy0 = src.barY + advance(srcCursor, `${e.fromCol}|${e.fromEvent}`);
    const sy1 = sy0 + srcH;
    srcCursor.set(`${e.fromCol}|${e.fromEvent}`, advance(srcCursor, `${e.fromCol}|${e.fromEvent}`) + srcH);
    const dy0 = dst.barY + advance(dstCursor, `${e.toCol}|${e.toEvent}`);
    const dy1 = dy0 + dstH;
    dstCursor.set(`${e.toCol}|${e.toEvent}`, advance(dstCursor, `${e.toCol}|${e.toEvent}`) + dstH);

    // Edge geometry: ribbon spans from src.right (x1) to dst.x (x2).
    const x1 = src.right;
    const x2 = dst.x;
    const xc1 = x1 + (x2 - x1) * 0.5;
    const xc2 = x1 + (x2 - x1) * 0.5;
    // Mixpanel-style path: straight stub on each end + cubic bezier middle.
    const stub = Math.min(24, (x2 - x1) * 0.1);
    const d =
      `M ${x1} ${sy0} ` +
      `H ${x1 + stub} ` +
      `C ${xc1} ${sy0} ${xc2} ${dy0} ${x2 - stub} ${dy0} ` +
      `H ${x2} ` +
      `V ${dy1} ` +
      `H ${x2 - stub} ` +
      `C ${xc2} ${dy1} ${xc1} ${sy1} ${x1 + stub} ${sy1} ` +
      `H ${x1} z`;

    paths.push({
      d,
      isDrop: e.isToDrop,
      // Mixpanel sets `is-secondary` on ribbons whose destination row in the
      // dest node is the lower (drop) breakdown rather than the upper (continue).
      isSecondary: e.isToDrop, // (close enough for our use)
      fromKey: `${e.fromCol}|${e.fromEvent}`,
      toKey:   `${e.toCol}|${e.toEvent}`,
    });
  }
  return paths;
}

function NodeBar({ node, height, hoverState, onHover, onLeave, onSegmentHover, onSegmentLeave, nodeKey, activeSeg }) {
  // Which sub-segment within this node is currently `.is-active`?
  // activeSeg from parent is { nodeKey, kind, … } — match on both.
  const isActiveContinue = activeSeg && activeSeg.nodeKey === nodeKey && activeSeg.kind === 'continue';
  const isActiveDrop     = activeSeg && activeSeg.nodeKey === nodeKey && activeSeg.kind === 'drop';
  // Two stacked segments: indigo continue (top) + coral drop (bottom). For
  // anchor / drop-only nodes only one segment is shown.
  const total = Math.max(1, node.continueW + node.dropW);
  const continuePct = (node.continueW / total) * 100;
  const dropPct     = (node.dropW     / total) * 100;
  const isPureDrop  = node.isDrop;
  const isAnchor    = node.isAnchor;

  // For the leftmost anchor node, Mixpanel shows the split-by-conversion
  // (% that reached step B vs dropped). For mid-flow nodes it shows the
  // forward-vs-drop split. For pure Drop-off nodes the segment is full coral.
  const continueH = Math.round((continuePct / 100) * height);
  const dropH     = height - continueH;

  // No outgoing edges (rightmost column) → show as single block (no split data).
  const hasSplit = (node.continueW + node.dropW) > 0;

  // Mixpanel-faithful node-bar dimensions — pulled from their shadow DOM via
  // the chrome-MCP probe: .sankey-node width 48px, border-radius 4px,
  // .sankey-node-breakdown-percentage fontSize 12px, fontWeight 400.
  // Mixpanel's `.sankey-node-breakdown.is-active` style: 2px white inner
  // ring + 3px chart-highlight outer ring. We approximate `--chart-highlight`
  // by re-using the segment's own colour (indigo for continue, coral for drop).
  // Toggled below only when segHover matches THIS node + matching segment kind.
  const segStyle = (bg, fg, h, active) => ({
    background: bg,
    color: fg,
    height: h + 'px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 600,
    fontFamily: 'Inter, system-ui, sans-serif',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    // is-active ring — 2px white inner + 3px coloured outer (Mixpanel's exact
    // box-shadow recipe from .sankey-node-breakdown.is-active).
    boxShadow: active ? `0 0 0 2px #FFFFFF, 0 0 0 3px ${bg}` : 'none',
    transition: 'box-shadow 120ms ease',
  });

  const fireSeg = (kind, pct) => () => onSegmentHover && onSegmentHover({
    nodeKey, kind, pct, eventName: node.event, isDrop: kind === 'drop',
  });
  const leaveSeg = () => onSegmentLeave && onSegmentLeave();

  return (
    <Box
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      width="48px"
      cursor="pointer"
      borderRadius="4px"
      overflow="hidden"
      style={{ filter: hoverState === 'dim' ? 'opacity(0.35)' : 'none' }}
    >
      {/* 10px font → text fits in 12px-tall segments. Below 12px we hide it.
          Each segment gets the .is-active ring style when hovered (Mixpanel parity). */}
      {isPureDrop ? (
        <div
          style={segStyle(C_GREY3, C_TEXT_LIGHT, height, isActiveDrop)}
          onMouseEnter={fireSeg('drop', 100)}
          onMouseLeave={leaveSeg}
        />
      ) : !hasSplit ? (
        <div
          style={segStyle(C_INDIGO, C_TEXT_LIGHT, height, isActiveContinue)}
          onMouseEnter={fireSeg('continue', 100)}
          onMouseLeave={leaveSeg}
        />
      ) : (
        <>
          {continueH > 0 && (
            <div
              style={segStyle(C_INDIGO, C_TEXT_LIGHT, continueH, isActiveContinue)}
              onMouseEnter={fireSeg('continue', continuePct)}
              onMouseLeave={leaveSeg}
            >
              {/* Hide 100% — it's the trivial single-segment case. Show real
                  splits (57.97% / 42.03%) since those carry information. */}
              {continueH >= 12 && continuePct < 99.99 ? fmtPct(continuePct) : ''}
            </div>
          )}
          {dropH > 0 && (
            <div
              style={segStyle(C_CORAL, C_TEXT_DARK, dropH, isActiveDrop)}
              onMouseEnter={fireSeg('drop', dropPct)}
              onMouseLeave={leaveSeg}
            >
              {dropH >= 12 && dropPct < 99.99 ? fmtPct(dropPct) : ''}
            </div>
          )}
        </>
      )}
    </Box>
  );
}

/**
 * Above-bar label card — matches Mixpanel's `.sankey-node-label-wrapper`
 * exactly (display flex column, text-align left, name 12px/700, pct 12px/400,
 * count 12px/400 in --gray3, optional lift indicator coloured green/red).
 */
/**
 * Two-row label card. Row 1 = event name (bold). Row 2 = percentage + count.
 * Fixed at exactly 32px tall (16 + 16) with 6px breathing below = the labelH
 * the geom code reserves. The bar is rendered AFTER this card with a strict
 * 6px margin-top, so it can never overlap the text.
 */
/**
 * Pick the Mixpanel-style event-type icon for a given node.
 *  - Drop-off       → down-arrow circle (Mixpanel: circle-decrease)
 *  - Other events   → grid icon (Mixpanel: table)
 *  - Anchor event   → right-arrow jump (Mixpanel: jump-right)
 *  - Regular event  → sparkle / activity (Mixpanel: event)
 */
function NodeTypeIcon({ node }) {
  if (node.isDrop)   return <FiArrowDownCircle size={11} />;
  if (node.isOther)  return <FiGrid size={11} />;
  if (node.isAnchor) return <FiArrowRight size={11} />;
  return <FiActivity size={11} />;
}

function NodeLabel({ node, columnTotal }) {
  const pct = (node.total / Math.max(1, columnTotal)) * 100;
  // Mixpanel-style plain-English tooltip text — describes what this node is
  // showing in human terms. Surfaces on hover via the Chakra Tooltip wrapper.
  const explainEvent = node.isDrop
    ? `${fmtPct(pct)} of users had no further recorded event at this step.`
    : node.isOther
      ? `${fmtPct(pct)} of users did an event that didn't rank in the top events at this step (${fmtCount(node.total)} users).`
      : node.isAnchor
        ? `${fmtCount(node.total)} users reached the anchor event "${node.event}" — the starting point of this flow.`
        : `${fmtPct(pct)} of users (${fmtCount(node.total)}) did "${node.event}" at this step.`;

  return (
    <Box
      style={{
        textAlign: 'center',
        fontFamily: 'Inter, system-ui, sans-serif',
        minWidth: 120,
        maxWidth: 180,
        marginBottom: 6,
      }}
    >
      <Tooltip label={explainEvent} placement="top" hasArrow openDelay={300}
        bg="#1F2937" color="#FFFFFF" fontSize="11px" px={2} py={1} borderRadius="md">
        <div
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 700, color: C_TEXT_DARK,
            lineHeight: '14px', whiteSpace: 'normal', wordBreak: 'break-word',
            overflow: 'visible', textAlign: 'center',
          }}
        >
          <span style={{ color: C_GREY3, display: 'inline-flex' }}><NodeTypeIcon node={node} /></span>
          <span>{node.event}</span>
        </div>
      </Tooltip>
      <Tooltip
        label={`${fmtPct(pct)} of anchor users (${fmtCount(node.total)} of total) did this at this step.`}
        placement="bottom" hasArrow openDelay={300}
        bg="#1F2937" color="#FFFFFF" fontSize="11px" px={2} py={1} borderRadius="md">
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 6, lineHeight: '16px',
          justifyContent: 'center',
        }}>
          <span style={{ fontSize: 12, fontWeight: 400, color: C_TEXT_DARK }}>
            {fmtPct(pct)}
          </span>
          <span style={{ fontSize: 12, fontWeight: 400, color: C_GREY3 }}>
            {fmtCount(node.total)}
          </span>
        </div>
      </Tooltip>
      {/* Lift indicator — % above (green ↑) or below (red ↓) the column's
          average ranked-event total. Mixpanel's exact pattern. */}
      {typeof node.lift === 'number' && Math.abs(node.lift) >= 1 && (
        <Tooltip
          label={node.lift >= 0
            ? `This event is ${node.lift.toFixed(2)}% MORE common at this step than the column average.`
            : `This event is ${Math.abs(node.lift).toFixed(2)}% LESS common at this step than the column average.`}
          placement="bottom" hasArrow openDelay={300}
          bg="#1F2937" color="#FFFFFF" fontSize="11px" px={2} py={1} borderRadius="md">
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
            fontSize: 11, fontWeight: 500, lineHeight: '14px', marginTop: 2,
            color: node.lift >= 0 ? C_LIFT_POS : C_LIFT_NEG,
          }}>
            <span style={{ fontFamily: 'system-ui', fontSize: 12 }}>
              {node.lift >= 0 ? '↑' : '↓'}
            </span>
            {Math.abs(node.lift).toFixed(2)}%
          </div>
        </Tooltip>
      )}
    </Box>
  );
}

/**
 * Wave SVG separator between bands — Mixpanel renders this when there's
 * "any sequence of events" between consecutive anchors. Visually it's a
 * stylised tilde / wave drawn at 22×22.
 */
function WaveSeparator({ height }) {
  return (
    <Box style={{
      width: 32, height,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: C_GREY3,
    }}>
      <svg viewBox="0 0 22 22" width={22} height={22}>
        <path d="M4 8a4.09 4.09 0 013.49-1.65c2.51 0 4.51 3.16 7 3.16A4.13 4.13 0 0018 8M4 13a4.09 4.09 0 013.49-1.61c2.51 0 4.51 3.15 7 3.15A4.14 4.14 0 0018 13"
          fill="none" stroke="currentColor" strokeLinecap="round" strokeMiterlimit={10} strokeWidth={1.5} />
      </svg>
    </Box>
  );
}

export default function FlowTile({ data, tile, onEdit }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(960);
  // Default tile height — bumped 25% from prior 640 → 800 (owner request).
  // Other tile types self-declare a fixed min-height too (LineChart 260,
  // FunnelTile 320, BreakdownBar variable). The board grid honours it.
  const [containerH, setContainerH] = useState(800);
  const [hover, setHover] = useState(null); // { type:'node', key } | { type:'path', idx }
  // Segment-level hover state — Mixpanel's `is-active` on .sankey-node-breakdown.
  // When set, we render a dark floating chip next to the segment showing
  // "57.97% Converted" / "42.03% Dropped" exactly like Mixpanel.
  const [activeSeg, setActiveSeg] = useState(null); // { nodeKey, kind, pct, eventName, isDrop }
  const [activeSegPos, setActiveSegPos] = useState(null); // { x, y }

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = Math.floor(e.contentRect.width);
        const h = Math.floor(e.contentRect.height);
        if (w > 0) setWidth(w);
        // Respect the tile container's height when one is set. We subtract
        // the legend + header strip + bottom padding to get the column band.
        // Bounded so the chart still has min vertical room for at least a
        // 280px-tall column band even on very short tiles.
        if (h > 200) setContainerH(Math.max(280, h - 96));
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Top-N is initially seeded from the tile config but the user can override
  // it locally via the inline chip — no save-back. Allows quick "show me more"
  // / "show me less" exploration without opening the editor.
  const [topNOverride, setTopNOverride] = useState(null);
  const topN = topNOverride ?? (data?.top_n_per_step || 5);
  const layout = useMemo(() => data ? buildLayout(data, topN) : null, [data, topN]);

  const geom = useMemo(() => {
    if (!layout) return null;
    const colCount = layout.columns.length;
    // Mixpanel measurements (from shadow DOM probe):
    //  .sankey-table-column.is-step-column → 128px wide, padding 6px 24px
    //  .sankey-node-wrapper → 120px wide, vertical (label above + bar below)
    //  .sankey-node → 48px wide bar
    //  margin-bottom between wrappers: 12px
    // Stretch the chart edge-to-edge: full container width is divided
    // exactly among columns. No left/right buffer — the leftmost chevron
    // starts at x=0, the rightmost chevron's right edge sits at x=width.
    const colWidth = Math.max(70, Math.floor(width / Math.max(colCount, 1)));
    // The node BAR sits in the right half of the column — centred under the
    // column header. We render the label card (120px wide) above the bar.
    const barOffset = Math.floor((colWidth - 48) / 2);
    const colOffsets = layout.columns.map((_, i) => i * colWidth + barOffset);
    const headerH = 40;
    // Layout invariant: label sits above bar with no overlap. Three rows:
    //   1. event name (wraps to 2 lines max @ 14px line-height = 28px)
    //   2. pct + count (16px)
    //   3. optional lift indicator (14px + 2px margin = 16px)
    // Inner container = labelH − 6 (margin to bar). Content = 28+16+16 = 60.
    // labelH = 70 → inner = 64 → 4 px headroom for the lift line. Stable
    // bar positions regardless of whether a node carries a lift value.
    const labelH = 70;
    const nodeGap = 8;
    const bandAvail = Math.max(160, containerH - 12);
    const colHeight = bandAvail;
    return {
      colOffsets,
      colWidth,
      colTop: headerH + 12,
      colHeight,
      nodeWidth: 48,
      labelH,
      nodeGap,
      headerH,
      // svgWidth equals colCount × colWidth exactly so it lines up flush
      // with the header chevron strip — no trailing whitespace stripe.
      svgWidth: colCount * colWidth,
      svgHeight: headerH + 12 + colHeight + 24,
    };
  }, [layout, width, containerH]);

  const positions = useMemo(() => {
    if (!layout || !geom) return null;
    return placeNodes(layout.columns, geom);
  }, [layout, geom]);

  const paths = useMemo(() => {
    if (!layout || !geom || !positions) return [];
    return buildEdgePaths(layout.columns, layout.edges, positions, geom);
  }, [layout, geom, positions]);

  if (!data) return null;
  if (!data.ribbons || data.ribbons.length === 0) {
    return <Text fontSize="sm" color="gray.500">No paths to show for this anchor event in the selected window.</Text>;
  }
  if (!layout || !geom || !positions) return null;

  // MIXPANEL HOVER MODEL — corrected via the owner's verified screenshot:
  //   Hovering a node lights up its FULL connected subgraph (upstream + down-
  //   stream BFS). My earlier CSS-only probe missed this because Mixpanel
  //   implements the subgraph highlight in JS class-toggling inside their Lit
  //   component, not via static CSS rules.
  //
  // Visual treatment matches the screenshot:
  //   • Connected nodes  : stay vivid (full colour, full opacity)
  //   • Unconnected nodes: fade to ~0.30 opacity (the washed-out lavender look)
  //   • Connected ribbons: light up in indigo (continue) or coral (drop)
  //   • Unconnected ribbons: stay grey at very low opacity
  //   • Hovered segment   : gets the `is-active` ring (white inner + colour outer)
  const litPaths = new Set();
  const litNodes = new Set();
  if (hover) {
    if (hover.type === 'node') {
      // ASYMMETRIC highlight model:
      //   • TOWARD the nearest anchor → walk multi-hop, stopping AT the anchor.
      //     This shows "where this event leads in the conversion journey".
      //   • AWAY from the nearest anchor → single hop only.
      //     Going deeper into the past (or beyond the post-anchor side)
      //     adds clutter without analytical value.
      //   • Hovered IS an anchor → multi-hop in BOTH directions (bounded by
      //     adjacent anchors for multi-anchor flows).
      const colOf = (key) => parseInt(key.split('|')[0], 10);
      const hoveredCol = colOf(hover.key);
      const anchorCols = layout.bands.map((b) => b.anchorCol).sort((a, b) => a - b);

      // Determine which direction is "toward" vs "away" from the nearest anchor.
      let towardForward = false;   // true if forward walk should be multi-hop
      let towardBackward = false;  // true if backward walk should be multi-hop
      let forwardBound = Infinity;
      let backwardBound = -Infinity;
      const isAnchor = anchorCols.includes(hoveredCol);
      if (isAnchor) {
        // Hovered IS an anchor → walk multi-hop both directions, bounded by
        // adjacent anchors so each band stays self-contained.
        towardForward = true; towardBackward = true;
        for (const aCol of anchorCols) {
          if (aCol > hoveredCol && aCol < forwardBound) forwardBound = aCol;
          if (aCol < hoveredCol && aCol > backwardBound) backwardBound = aCol;
        }
      } else {
        // Find nearest anchor to determine direction.
        let nearestAnchor = anchorCols[0];
        let nearestDist = Math.abs(anchorCols[0] - hoveredCol);
        for (const aCol of anchorCols) {
          const d = Math.abs(aCol - hoveredCol);
          if (d < nearestDist) { nearestDist = d; nearestAnchor = aCol; }
        }
        if (nearestAnchor > hoveredCol) {
          // Anchor is to the RIGHT → forward is "toward" (multi-hop to anchor).
          towardForward = true;
          forwardBound = nearestAnchor;
        } else {
          // Anchor is to the LEFT → backward is "toward" (multi-hop to anchor).
          towardBackward = true;
          backwardBound = nearestAnchor;
        }
      }

      litNodes.add(hover.key);
      const incoming = new Map();
      const outgoing = new Map();
      paths.forEach((p, i) => {
        if (!outgoing.has(p.fromKey)) outgoing.set(p.fromKey, []);
        outgoing.get(p.fromKey).push(i);
        if (!incoming.has(p.toKey)) incoming.set(p.toKey, []);
        incoming.get(p.toKey).push(i);
      });

      // FORWARD direction
      if (towardForward) {
        // Multi-hop walk forward to anchor.
        const downQ = [hover.key];
        while (downQ.length) {
          const cur = downQ.shift();
          for (const i of outgoing.get(cur) || []) {
            if (litPaths.has(i)) continue;
            const dst = paths[i].toKey;
            if (colOf(dst) > forwardBound) continue;
            litPaths.add(i);
            if (!litNodes.has(dst)) { litNodes.add(dst); downQ.push(dst); }
          }
        }
      } else {
        // Single hop forward.
        for (const i of outgoing.get(hover.key) || []) {
          litPaths.add(i);
          litNodes.add(paths[i].toKey);
        }
      }

      // BACKWARD direction
      if (towardBackward) {
        // Multi-hop walk backward to anchor.
        const upQ = [hover.key];
        while (upQ.length) {
          const cur = upQ.shift();
          for (const i of incoming.get(cur) || []) {
            if (litPaths.has(i)) continue;
            const src = paths[i].fromKey;
            if (colOf(src) < backwardBound) continue;
            litPaths.add(i);
            if (!litNodes.has(src)) { litNodes.add(src); upQ.push(src); }
          }
        }
      } else {
        // Single hop backward.
        for (const i of incoming.get(hover.key) || []) {
          litPaths.add(i);
          litNodes.add(paths[i].fromKey);
        }
      }
    } else if (hover.type === 'path') {
      litPaths.add(hover.idx);
      const p = paths[hover.idx];
      litNodes.add(p.fromKey);
      litNodes.add(p.toKey);
    }
  }

  return (
    <Box
      ref={containerRef}
      position="relative"
      width="100%"
      style={{ overflow: 'hidden', minHeight: 800, height: '100%', paddingTop: 0 }}
    >
      {/* Legend — honest about what we actually compute. The indigo segment
          inside each node = % of users at that node who had ANY next event in
          the window. The coral segment = % whose path stopped here (no further
          event recorded). This is NOT the same as Mixpanel's anchor-to-anchor
          conversion, which needs per-contact path tracing the backend doesn't
          currently emit. Switch the labels back once that calculation lands. */}
      {/* Toolbar row: legend (when applicable) on the left, top-N chip on the right. */}
      <Box style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12,
        color: C_TEXT_DARK,
        marginTop: -4,
        marginBottom: 10,
      }}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {layout.bands.length > 1 && (
            <>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 12, height: 12, background: C_INDIGO, borderRadius: 2, display: 'inline-block' }} />
                Continued
              </Box>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 12, height: 12, background: C_CORAL, borderRadius: 2, display: 'inline-block' }} />
                Dropped off
              </Box>
            </>
          )}
        </Box>
        {/* Top-N inline chip — Mixpanel's "5 rows" pill. Lets the user widen
            or narrow the events-per-step without opening the editor. */}
        <Tooltip label="How many top events to show per step. Lower = cleaner, higher = more long-tail visibility."
          placement="left" hasArrow openDelay={300}
          bg="#1F2937" color="#FFFFFF" fontSize="11px" px={2} py={1} borderRadius="md">
          <Box style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: '#F3F4F6', borderRadius: 999, padding: '2px 8px 2px 10px',
            border: '1px solid #E5E7EB',
            fontSize: 12, color: C_TEXT_DARK, fontWeight: 500,
          }}>
            <span>Top</span>
            <select
              value={topN}
              onChange={(e) => setTopNOverride(parseInt(e.target.value, 10))}
              style={{
                background: 'transparent', border: 'none', outline: 'none',
                fontSize: 12, color: C_INDIGO, fontWeight: 600, cursor: 'pointer',
                paddingRight: 2,
              }}
            >
              {[3, 5, 7, 10, 12].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span>events / step</span>
          </Box>
        </Tooltip>
      </Box>

      {/* Column header strip — Mixpanel-style chevron pills tinted --gray1.
          Edge "+" buttons let the user open the editor to add steps; per-column
          "−" appears on hover when there's more than one step to remove. */}
      <Box
        position="relative"
        height={`${geom.headerH}px`}
        mb="2px"
        style={{ width: geom.svgWidth, display: 'flex', alignItems: 'center', background: '#FFFFFF' }}
      >
        {/* Leftmost + (add step before) */}
        <Tooltip label="Add a step before the first anchor" hasArrow placement="top" openDelay={200}>
          <IconButton size="xs" variant="ghost" aria-label="Add step before"
            icon={<FiPlus />} color="var(--brand-primary)"
            onClick={() => onEdit && onEdit(tile)}
            style={{ position: 'absolute', left: -28, top: '50%', transform: 'translateY(-50%)' }} />
        </Tooltip>
        {layout.columns.map((col, i) => {
          const isLast = i === layout.columns.length - 1;
          const chevTip = 16;
          const clip = isLast
            ? `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${chevTip}px 50%)`
            : i === 0
              ? `polygon(0 0, calc(100% - ${chevTip}px) 0, 100% 50%, calc(100% - ${chevTip}px) 100%, 0 100%)`
              : `polygon(0 0, calc(100% - ${chevTip}px) 0, 100% 50%, calc(100% - ${chevTip}px) 100%, 0 100%, ${chevTip}px 50%)`;
          // Tooltip body — describes which step-offset this column is.
          const tooltipLabel = col.isAnchor
            ? `Step ${col.bandCode} — anchor event "${col.headerEvent}"`
            : `Step ${col.headerCode} — ${col.headerCode.includes('+') ? 'event happening AFTER the anchor' : 'event happening BEFORE the anchor'}`;
          return (
            <Tooltip key={col.colIdx} label={tooltipLabel} hasArrow placement="top" openDelay={400}
              bg="#1F2937" color="#FFFFFF" fontSize="11px" px={2} py={1} borderRadius="md">
              <Box
                style={{
                  width: geom.colWidth,
                  height: geom.headerH,
                  background: C_GRAY1,
                  clipPath: clip,
                  WebkitClipPath: clip,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: 12,
                  color: C_GREY3,
                  marginRight: 1,
                  cursor: 'default',
                }}
              >
                {col.isAnchor ? (
                  <>
                    <span style={{
                      background: 'var(--brand-tint-strong)', color: C_INDIGO,
                      borderRadius: 999, padding: '2px 8px',
                      fontSize: 12, fontWeight: 600,
                    }}>{col.bandCode}</span>
                    <span style={{ color: C_TEXT_DARK, fontWeight: 400 }}>
                      {col.headerEvent}
                    </span>
                  </>
                ) : (
                  col.headerCode
                )}
              </Box>
            </Tooltip>
          );
        })}
        {/* Rightmost + (add step after) */}
        <Tooltip label="Add a step after the last anchor" hasArrow placement="top" openDelay={200}>
          <IconButton size="xs" variant="ghost" aria-label="Add step after"
            icon={<FiPlus />} color="var(--brand-primary)"
            onClick={() => onEdit && onEdit(tile)}
            style={{ position: 'absolute', right: -28, top: '50%', transform: 'translateY(-50%)' }} />
        </Tooltip>
      </Box>

      {/* Chart body: columns of labels+bars, with overlay SVG for ribbons. */}
      <Box position="relative" style={{ width: geom.svgWidth, height: geom.colHeight + 20 }}>
        {/* SVG ribbons */}
        <svg
          width={geom.svgWidth}
          height={geom.colHeight + 20}
          style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}
        >
          {paths.map((p, i) => {
            // Hover treatment:
            //   no hover         → grey at 0.10 (full chart context)
            //   connected/lit    → indigo (continue) or coral (drop) at 0.55
            //   direct hover     → same colour at 0.75 (slight emphasis)
            //   unconnected      → HIDDEN (opacity 0) so the highlighted path
            //                      reads cleanly without faint distractor lines
            const isLit = hover ? litPaths.has(i) : false;
            const isDirect = hover && hover.type === 'path' && hover.idx === i;
            let fill, opacity;
            if (!hover) {
              fill = C_GREY3; opacity = RIBBON_REST_OPACITY;
            } else if (isLit) {
              fill = p.isDrop ? C_CORAL : C_INDIGO;
              opacity = isDirect ? 0.75 : 0.55;
            } else {
              fill = C_GREY3; opacity = 0;
            }
            return (
              <path
                key={i}
                d={p.d}
                fill={fill}
                fillOpacity={opacity}
                style={{ pointerEvents: 'auto', cursor: 'pointer', transition: 'fill 120ms ease, fill-opacity 120ms ease' }}
                onMouseEnter={() => setHover({ type: 'path', idx: i })}
                onMouseLeave={() => setHover(null)}
              />
            );
          })}
        </svg>

        {/* Per-column node-wrapper — label card on top, bar below.
            Matches Mixpanel's `.sankey-node-wrapper` layout exactly:
            display:flex; flex-direction:column; align-items:flex-start. */}
        {layout.columns.map((col) => {
          return col.nodes.map((node) => {
            const pos = positions.get(`${col.colIdx}|${node.event}`);
            if (!pos) return null;
            const key = `${col.colIdx}|${node.event}`;
            // Mixpanel-style: when ANY hover is active, nodes NOT in the
            // connected subgraph fade to ~0.30 opacity (washed-out lavender).
            // Connected ones stay vivid. This matches the owner's screenshot.
            const isLit = litNodes.has(key);
            const dim = hover && !isLit;
            // Label position: above-left of the bar, aligned to the bar's left edge.
            // Centre the entire wrapper (120 px label card on top + 48 px bar
            // beneath) on the column's bar-X axis. The wrapper's x is shifted
            // left by (120-48)/2 = 36 px so the bar lands at pos.x.
            // Render label and bar as INDEPENDENT absolutely-positioned
            // siblings. Critical: the bar must sit at exactly pos.barY in
            // pixels, regardless of how much vertical space the label took.
            // Otherwise ribbons (computed against pos.barY) drift out of the
            // bar when long event names wrap to multiple lines.
            return (
              <React.Fragment key={key}>
                <Box
                  position="absolute"
                  left={`${pos.x - 36}px`}
                  top={`${pos.labelY}px`}
                  style={{
                    width: 120,
                    // Strict bound — label content beyond labelH is clipped
                    // so it never visually crosses into the bar's space.
                    // Most event names fit in 2 wrapped lines at 11px.
                    height: geom.labelH - 6,
                    overflow: 'hidden',
                    opacity: dim ? 0.3 : 1,
                    transition: 'opacity 120ms ease',
                    pointerEvents: 'none',
                  }}
                >
                  <NodeLabel node={node} columnTotal={col.pctDenom || col.total} />
                </Box>
                <Box
                  position="absolute"
                  left={`${pos.x}px`}
                  top={`${pos.barY}px`}
                  style={{
                    opacity: dim ? 0.3 : 1,
                    transition: 'opacity 120ms ease',
                  }}
                >
                  <NodeBar
                    node={node}
                    nodeKey={key}
                    activeSeg={activeSeg}
                    height={pos.h}
                    hoverState={hover && !isLit ? 'dim' : 'on'}
                    onHover={() => setHover({ type: 'node', key })}
                    onLeave={() => setHover(null)}
                    onSegmentHover={(seg) => {
                      setActiveSeg(seg);
                      const cx = pos.right + 18;
                      const cy = pos.barY + pos.h / 2;
                      setActiveSegPos({ x: cx, y: cy });
                    }}
                    onSegmentLeave={() => { setActiveSeg(null); setActiveSegPos(null); }}
                  />
                </Box>
              </React.Fragment>
            );
          });
        })}

        {/* Mixpanel-style floating "X% Converted / X% Dropped" chip — only
            renders while a node segment is actively hovered (is-active). */}
        {activeSeg && activeSegPos && (
          <Box
            position="absolute"
            left={`${activeSegPos.x}px`}
            top={`${activeSegPos.y}px`}
            style={{
              transform: 'translateY(-50%)',
              background: '#1F2937',
              color: '#FFFFFF',
              padding: '6px 10px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Inter, system-ui, sans-serif',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(15,23,42,0.25)',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            <Box as="span" style={{
              display: 'inline-block',
              width: 8, height: 8, borderRadius: 2, marginRight: 6,
              background: activeSeg.isDrop ? C_CORAL : C_INDIGO,
              verticalAlign: 'middle',
            }} />
            {fmtPct(activeSeg.pct)} {activeSeg.isDrop ? 'Dropped' : 'Converted'}
          </Box>
        )}
      </Box>
    </Box>
  );
}
